/**
 * API Client for Talk Australia WS-4 Prospect Portal
 * Handles all backend communication with proper error handling and request formatting
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://portal-api.talkaustralia.com.au';

/**
 * Generic fetch wrapper with error handling
 */
async function fetchAPI(endpoint, options = {}) {
  const {
    method = 'GET',
    headers = {},
    body = null,
    token = null,
    timeout = 30000,
  } = options;

  const finalHeaders = {
    'Content-Type': 'application/json',
    ...headers,
  };

  if (token) {
    finalHeaders['Authorization'] = `Bearer ${token}`;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: finalHeaders,
      body: body ? JSON.stringify(body) : null,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.message || `API Error: ${response.status}`);
      error.status = response.status;
      error.data = errorData;
      throw error;
    }

    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout');
    }
    throw error;
  }
}

/**
 * Prospect API endpoints
 */
export const prospectAPI = {
  /**
   * Fetch prospect data by public token (no auth required)
   */
  async getProspectByToken(token) {
    return fetchAPI(`/api/prospects/token/${token}`);
  },

  /**
   * Fetch full prospect details including section config
   */
  async getProspectDetails(prospectId) {
    return fetchAPI(`/api/prospects/${prospectId}`);
  },

  /**
   * Check if prospect has accepted the proposal
   */
  async checkProspectStatus(prospectId) {
    return fetchAPI(`/api/prospects/${prospectId}/status`);
  },
};

/**
 * Tracking API endpoints - records analytics about prospect engagement
 */
export const trackingAPI = {
  /**
   * Record that prospect viewed the portal
   */
  async recordView(prospectId, sessionId) {
    return fetchAPI('/api/tracking/view', {
      method: 'POST',
      body: {
        prospectId,
        sessionId,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      },
    });
  },

  /**
   * Record scroll depth for a specific section
   */
  async recordSectionScroll(prospectId, sessionId, sectionId, scrollPercentage) {
    return fetchAPI('/api/tracking/scroll', {
      method: 'POST',
      body: {
        prospectId,
        sessionId,
        sectionId,
        scrollPercentage,
        timestamp: new Date().toISOString(),
      },
    });
  },

  /**
   * Record time spent on page
   */
  async recordTimeOnPage(prospectId, sessionId, totalSeconds, sectionTimes) {
    return fetchAPI('/api/tracking/time', {
      method: 'POST',
      body: {
        prospectId,
        sessionId,
        totalSeconds,
        sectionTimes, // { sectionId: seconds, ... }
        timestamp: new Date().toISOString(),
      },
    });
  },

  /**
   * Record section view (when section becomes visible)
   */
  async recordSectionView(prospectId, sessionId, sectionId) {
    return fetchAPI('/api/tracking/section-view', {
      method: 'POST',
      body: {
        prospectId,
        sessionId,
        sectionId,
        timestamp: new Date().toISOString(),
      },
    });
  },
};

/**
 * Chat API endpoints
 */
export const chatAPI = {
  /**
   * Send a message to the prospect's chat
   */
  async sendMessage(prospectId, message, senderName = 'Prospect') {
    return fetchAPI('/api/chat/send', {
      method: 'POST',
      body: {
        prospectId,
        message,
        senderName,
        timestamp: new Date().toISOString(),
      },
    });
  },

  /**
   * Fetch message history for a prospect
   */
  async getMessages(prospectId, limit = 50) {
    return fetchAPI(`/api/chat/${prospectId}/messages?limit=${limit}`);
  },

  /**
   * Poll for new messages (real-time-ish via polling)
   */
  async pollNewMessages(prospectId, sinceTimestamp) {
    return fetchAPI(`/api/chat/${prospectId}/messages?since=${sinceTimestamp}`);
  },
};

/**
 * E-sign API endpoints
 */
export const esignAPI = {
  /**
   * Submit signed proposal
   */
  async submitSignature(prospectId, signatureData) {
    return fetchAPI('/api/esign/submit', {
      method: 'POST',
      body: {
        prospectId,
        ...signatureData,
        timestamp: new Date().toISOString(),
      },
    });
  },

  /**
   * Get signature status
   */
  async getSignatureStatus(prospectId) {
    return fetchAPI(`/api/esign/${prospectId}/status`);
  },
};

/**
 * Configuration API endpoints (admin only - Amit's configurator)
 */
export const configAPI = {
  /**
   * Fetch all section configurations
   */
  async getSectionConfigs() {
    return fetchAPI('/api/config/sections');
  },

  /**
   * Update section configuration for a prospect
   */
  async updateSectionConfig(prospectId, sectionConfig) {
    return fetchAPI(`/api/config/prospects/${prospectId}/sections`, {
      method: 'PUT',
      body: { sectionConfig },
    });
  },

  /**
   * Update prospect tier
   */
  async updateProspectTier(prospectId, tier) {
    return fetchAPI(`/api/config/prospects/${prospectId}/tier`, {
      method: 'PUT',
      body: { tier },
    });
  },

  /**
   * Reorder sections
   */
  async reorderSections(prospectId, sectionOrder) {
    return fetchAPI(`/api/config/prospects/${prospectId}/reorder`, {
      method: 'PUT',
      body: { sectionOrder },
    });
  },

  /**
   * Preview portal with different config
   */
  async previewConfig(prospectId, config) {
    return fetchAPI(`/api/config/prospects/${prospectId}/preview`, {
      method: 'POST',
      body: { config },
    });
  },
};

/**
 * Error helpers
 */
export const apiErrors = {
  isTokenExpired: (error) => error?.status === 401 || error?.data?.code === 'TOKEN_EXPIRED',
  isNotFound: (error) => error?.status === 404,
  isServerError: (error) => error?.status >= 500,
  isNetworkError: (error) => error?.message === 'Request timeout' || !navigator.onLine,
};

const apiModule = {
  prospectAPI,
  trackingAPI,
  chatAPI,
  esignAPI,
  configAPI,
  apiErrors,
};

export default apiModule;
